// src/pages/Billing.js
import React from 'react';
import { useAuth } from '../hooks/useAuth';
import { Check, Zap } from 'lucide-react';

const FREE_FEATURES = [
  'Up to 50 sessions / month',
  'Basic analytics dashboard',
  'QR code generation',
  'Session history (30 days)',
  'Email support',
];

const PRO_FEATURES = [
  'Unlimited sessions',
  'Full analytics + heatmaps',
  'Session history (unlimited)',
  'Weekly email reports',
  'Multi-staff logins',
  'POS integration (coming soon)',
  'Priority support',
];

export default function Billing() {
  const { merchant } = useAuth();
  const isPro = merchant?.plan === 'paid';

  return (
    <div>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '32px', fontWeight: 500, color: '#1a1a1a', marginBottom: '6px' }}>
          Billing
        </h1>
        <p style={{ fontSize: '14px', color: '#888' }}>Manage your plan and billing details.</p>
      </div>

      {/* Current plan banner */}
      <div style={{
        background: isPro ? 'rgba(64,0,228,0.06)' : '#fff',
        borderRadius: '16px', padding: '24px',
        border: `1px solid ${isPro ? 'rgba(64,0,228,0.2)' : 'rgba(0,0,0,0.06)'}`,
        marginBottom: '32px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px',
      }}>
        <div>
          <div style={{ fontSize: '13px', color: '#888', marginBottom: '4px' }}>Current Plan</div>
          <div style={{ fontSize: '22px', fontWeight: 700, color: '#1a1a1a', fontFamily: "'Cormorant Garamond', serif" }}>
            {isPro ? '⚡ Pro Plan' : 'Free Plan'}
          </div>
          <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>
            {isPro ? 'Unlimited sessions · All features included' : '50 sessions / month · Basic features'}
          </div>
        </div>
        {isPro && (
          <button style={{
            padding: '10px 24px', background: 'transparent', color: '#dc2626',
            border: '1px solid rgba(220,38,38,0.3)', borderRadius: '10px',
            fontFamily: "'Outfit', sans-serif", fontSize: '13px', cursor: 'pointer',
          }}>
            Cancel Plan
          </button>
        )}
      </div>

      {/* Plan cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>

        {/* Free */}
        <div style={{
          background: '#fff', borderRadius: '20px', padding: '32px',
          border: `2px solid ${!isPro ? '#4000E4' : 'rgba(0,0,0,0.06)'}`,
          position: 'relative',
        }}>
          {!isPro && (
            <div style={{
              position: 'absolute', top: '-12px', left: '24px',
              background: '#4000E4', color: '#fff',
              fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
              padding: '4px 14px', borderRadius: '20px',
            }}>
              Current Plan
            </div>
          )}
          <div style={{ marginBottom: '8px', fontSize: '18px', fontWeight: 700, color: '#1a1a1a' }}>Free</div>
          <div style={{ marginBottom: '24px' }}>
            <span style={{ fontSize: '36px', fontWeight: 800, color: '#1a1a1a', fontFamily: "'Cormorant Garamond', serif" }}>$0</span>
            <span style={{ fontSize: '14px', color: '#888' }}> / month</span>
          </div>
          <div style={{ marginBottom: '28px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {FREE_FEATURES.map(f => (
              <div key={f} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: '#555' }}>
                <Check size={14} color="#16a34a" style={{ flexShrink: 0 }} />
                {f}
              </div>
            ))}
          </div>
          <button
            disabled={!isPro}
            style={{
              width: '100%', padding: '12px',
              background: !isPro ? 'rgba(0,0,0,0.05)' : 'transparent',
              color: '#888', border: '1px solid rgba(0,0,0,0.1)',
              borderRadius: '12px', fontFamily: "'Outfit', sans-serif",
              fontSize: '14px', fontWeight: 600, cursor: isPro ? 'pointer' : 'default',
            }}
          >
            {!isPro ? 'Current Plan' : 'Downgrade'}
          </button>
        </div>

        {/* Pro */}
        <div style={{
          background: '#1a1a1a', borderRadius: '20px', padding: '32px',
          border: `2px solid ${isPro ? '#4000E4' : '#1a1a1a'}`,
          position: 'relative',
        }}>
          {isPro && (
            <div style={{
              position: 'absolute', top: '-12px', left: '24px',
              background: '#4000E4', color: '#fff',
              fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
              padding: '4px 14px', borderRadius: '20px',
            }}>
              Current Plan
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Zap size={16} color="#C9A84C" fill="#C9A84C" />
            <span style={{ fontSize: '18px', fontWeight: 700, color: '#fff' }}>Pro</span>
          </div>
          <div style={{ marginBottom: '24px' }}>
            <span style={{ fontSize: '36px', fontWeight: 800, color: '#fff', fontFamily: "'Cormorant Garamond', serif" }}>$29</span>
            <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}> / month</span>
          </div>
          <div style={{ marginBottom: '28px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {PRO_FEATURES.map(f => (
              <div key={f} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>
                <Check size={14} color="#C9A84C" style={{ flexShrink: 0 }} />
                {f}
              </div>
            ))}
          </div>
          <button
            onClick={() => alert('Stripe billing coming soon! Email contact@checksintel.com to upgrade manually.')}
            style={{
              width: '100%', padding: '12px',
              background: isPro ? 'rgba(255,255,255,0.1)' : '#4000E4',
              color: '#fff', border: 'none',
              borderRadius: '12px', fontFamily: "'Outfit', sans-serif",
              fontSize: '14px', fontWeight: 600, cursor: 'pointer',
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            {isPro ? 'Current Plan' : 'Upgrade to Pro →'}
          </button>
        </div>
      </div>

      <p style={{ fontSize: '12px', color: '#bbb', marginTop: '24px', textAlign: 'center' }}>
        Questions about billing? Email us at{' '}
        <a href="mailto:contact@checksintel.com" style={{ color: '#4000E4' }}>contact@checksintel.com</a>
      </p>
    </div>
  );
}
